import React from 'react';
import AlignedPhotoFrame from '../AlignedPhotoFrame';
import { CardShell, BrandFooter, DeltaHero, PhotoTag, StatChip, scaleFor } from '../CardBits';

// Template B — Editorial.
// The number is the headline, the photos are the evidence. In portrait the
// headline sits ABOVE the photos; in square and wide it moves BESIDE them.
//
// EXPORT SAFETY: no object-fit / clip-path / filter. See lib/photoRender.js.
export default function EditorialCard({ model, W, H, form }) {
  const s = scaleFor(H);
  const pad = 44 * s;
  const stacked = form === 'portrait';
  const railW = stacked ? 0 : (form === 'wide' ? W * 0.34 : W * 0.36);
  const gap = 18 * s;

  const headline = (
    <div>
      <DeltaHero hero={model.hero} s={s} align="left" />
      {model.bodyFatDeltaLabel && (
        <div style={{ fontSize: 28 * s, opacity: 0.45, letterSpacing: 2 * s, marginTop: 8 * s }}>
          {model.bodyFatDeltaLabel}% {model.labels.bodyFat}
        </div>
      )}
      {/* The whole row collapses when numbers are hidden. Rendering "START —"
          would advertise that data was redacted, which is worse on a public
          post than the number itself, and it contradicts the model contract
          that a null label means the slot disappears (see SplitCard). */}
      {model.showNumbers && (model.before.weightLabel || model.after.weightLabel || model.bodyFatDeltaLabel) && (
        <div style={{ display: 'flex', gap: 10 * s, marginTop: 22 * s }}>
          {model.before.weightLabel && <StatChip s={s} value={model.before.weightLabel} label={model.labels.start} />}
          {model.after.weightLabel && <StatChip s={s} value={model.after.weightLabel} label={model.labels.now} tone="accent" />}
          {model.bodyFatDeltaLabel && <StatChip s={s} value={model.bodyFatDeltaLabel} label={model.labels.bodyFatShort} />}
        </div>
      )}
    </div>
  );

  const photos = (slotW, slotH) => (
    <div style={{ display: 'flex', gap, width: slotW, height: slotH }}>
      {[
        { side: model.before, tone: 'dark', label: model.labels.before },
        { side: model.after, tone: 'accent', label: model.labels.after },
      ].map(({ side, tone, label }) => {
        const halfW = (slotW - gap) / 2;
        return (
          <div key={label} style={{ position: 'relative', width: halfW, height: slotH }}>
            <AlignedPhotoFrame
              url={side.url}
              backdropUrl={side.backdropUrl}
              canonical={side.canonical}
              manual={side.manual}
              natural={side.natural}
              slotW={halfW}
              slotH={slotH}
              radius={22 * s}
            />
            {tone === 'accent' && (
              <div
                style={{
                  position: 'absolute',
                  left: 0, top: 0, width: halfW, height: slotH,
                  borderRadius: 22 * s,
                  border: `${3 * s}px solid rgba(255,92,0,0.5)`,
                  boxSizing: 'border-box',
                }}
              />
            )}
            <PhotoTag tone={tone} s={s} style={{ left: 16 * s, top: 16 * s }}>
              {side.dateLabel || label}
            </PhotoTag>
          </div>
        );
      })}
    </div>
  );

  const footerH = 70 * s;

  if (stacked) {
    // The stat row collapses whenever numbers are hidden (the coach's
    // default), but a fixed reserved height doesn't follow it — that left a
    // third of the card empty black. Give the headline block less height
    // when the row isn't there so photoH (computed below) takes the
    // difference instead.
    const hasStatRow = model.showNumbers
      && (model.before.weightLabel || model.after.weightLabel || model.bodyFatDeltaLabel);
    const headlineH = (hasStatRow ? 420 : 210) * s;
    const photoH = H - pad * 2 - headlineH - footerH;
    return (
      <CardShell W={W} H={H} padding={pad} background="#101012">
        <div style={{ height: headlineH }}>{headline}</div>
        {photos(W - pad * 2, photoH)}
        <div style={{ marginTop: 'auto', paddingTop: 20 * s }}>
          <BrandFooter handleLabel={model.handleLabel} s={s} />
        </div>
      </CardShell>
    );
  }

  const contentH = H - pad * 2 - footerH;
  return (
    <CardShell W={W} H={H} padding={pad} background="#101012">
      <div style={{ display: 'flex', gap: 30 * s, height: contentH }}>
        <div style={{ width: railW, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {headline}
        </div>
        {photos(W - pad * 2 - railW - 30 * s, contentH)}
      </div>
      <div style={{ marginTop: 'auto', paddingTop: 20 * s }}>
        <BrandFooter handleLabel={model.handleLabel} s={s} right={model.poseLabel} />
      </div>
    </CardShell>
  );
}
